import { NavigationSection } from 'src/pdp/components/GalleryNavigationBar/GalleryNavigationBar';
import { HotspotData } from 'src/types/ProductDetail.type';
import { create } from 'zustand';

interface FeatureHotspotsState {
  viewType: NavigationSection;
  hotspots: HotspotData;
  activeHotspot?: string;
}

export const INITIAL_STATE: FeatureHotspotsState = {
  viewType: 'exterior',
  hotspots: {
    exterior: [],
    interior: [],
  },
  activeHotspot: undefined,
};

interface FeatureHotspotsFunctions {
  setViewType: (section: NavigationSection) => void;
  onClickFeatureHotspot: (feature: string) => void;
  onClickPreviousFeatureHotspot: () => void;
  onClickNextFeatureHotspot: () => void;
  onCloseFeatureHotspots: () => void;
  reset: () => void;
  getNormalisedHotspots: () => { name: string; description?: string; image?: string; active: boolean }[];
  setHotspots: (hotspots: HotspotData) => void;
}

type FeatureHotspotsStore = FeatureHotspotsState & FeatureHotspotsFunctions;

export const useFeatureHotspotsStore = create<FeatureHotspotsStore>((set, getState) => ({
  ...INITIAL_STATE,

  reset: () => set(INITIAL_STATE),

  // setting viewtype manually will set the activeHotspot to undefined
  setViewType: (section) => set({ viewType: section }),
  onClickFeatureHotspot: (feature) => {
    const { activeHotspot } = getState();
    if (!activeHotspot) {
      return set({ activeHotspot: feature });
    }
  },

  onClickNextFeatureHotspot: () => {
    const { hotspots, activeHotspot } = getState();
    const hotspotNames = [
      ...hotspots.exterior.map((hotspot) => ({ name: hotspot.name, type: 'exterior' as NavigationSection })),
      ...hotspots.interior.map((hotspot) => ({ name: hotspot.name, type: 'interior' as NavigationSection })),
    ];

    const activeIndex = hotspotNames.findIndex((hotspot) => hotspot.name === activeHotspot);
    const nextIndex = (activeIndex + 1) % hotspotNames.length;
    const nextHotspot = hotspotNames[nextIndex];

    set({
      activeHotspot: nextHotspot.name,
      viewType: nextHotspot.type,
    });
  },

  onClickPreviousFeatureHotspot: () => {
    const { hotspots, activeHotspot } = getState();
    const hotspotNames = [
      ...hotspots.exterior.map((hotspot) => ({ name: hotspot.name, type: 'exterior' as NavigationSection })),
      ...hotspots.interior.map((hotspot) => ({ name: hotspot.name, type: 'interior' as NavigationSection })),
    ];

    const activeIndex = hotspotNames.findIndex((hotspot) => hotspot.name === activeHotspot);
    const previousIndex = (activeIndex - 1 + hotspotNames.length) % hotspotNames.length;
    const previousHotspot = hotspotNames[previousIndex];

    set({
      activeHotspot: previousHotspot.name,
      viewType: previousHotspot.type,
    });
  },

  onCloseFeatureHotspots: () =>
    set({
      activeHotspot: undefined,
    }),

  /**
   * Helper function to return all hotspots with a standard format without pointer data (only name, description, image)
   */
  getNormalisedHotspots: () => {
    const { hotspots, activeHotspot } = getState();
    return hotspots.exterior
      .map((hotspot) => ({
        name: hotspot.name,
        description: hotspot.description,
        image: hotspot.image,
        active: hotspot.name === activeHotspot,
      }))
      .concat(
        hotspots.interior.map((hotspot) => ({
          name: hotspot.name,
          description: hotspot.description,
          image: hotspot.image,
          active: hotspot.name === activeHotspot,
        })),
      );
  },

  setHotspots: (hotspots) => set({ hotspots }),
}));
